<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h4 class="mb-0">
        Мониторинг процессов
      </h4>
      <div class="d-flex align-items-center">
        <b-form-select
          v-model="statusFilter"
          size="sm"
          class="mr-1"
          style="width: 180px"
          :options="statusOptions"
          @change="load"
        />
        <b-button
          size="sm"
          variant="outline-primary"
          @click="load"
        >
          <feather-icon icon="RefreshCwIcon" /> Обновить
        </b-button>
      </div>
    </div>

    <b-table
      responsive
      small
      hover
      :busy="loading"
      :fields="fields"
      :items="instances"
      :per-page="perPage"
      :current-page="currentPage"
      show-empty
      empty-text="Нет экземпляров"
      :tbody-tr-class="rowClass"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner small class="align-middle mr-50" /> Загрузка…
        </div>
      </template>
      <template #cell(status_code)="data">
        <b-badge :variant="cubus.bpmStatusVariant(data.value)">
          {{ cubus.bpmStatusLabel(data.value) }}
        </b-badge>
      </template>
      <template #cell(actions)="data">
        <b-button
          size="sm"
          variant="outline-primary"
          @click="openInstance(data.item.id)"
        >
          Детально
        </b-button>
      </template>
    </b-table>
    <b-pagination
      v-if="instances.length > perPage"
      v-model="currentPage"
      :total-rows="instances.length"
      :per-page="perPage"
      align="center"
      size="sm"
      class="mt-1"
    />

    <!-- Детали экземпляра — в модальном окне (а не снизу под таблицей) -->
    <b-modal
      v-model="detailShow"
      size="xl"
      scrollable
      hide-footer
      :title="detail ? `${detailInstance.proc_name} · экземпляр #${detailInstance.id}` : 'Экземпляр'"
    >
      <div v-if="detail">
        <div class="mb-1">
          <b-badge :variant="cubus.bpmStatusVariant(detailInstance.status_code)">
            {{ cubus.bpmStatusLabel(detailInstance.status_code) }}
          </b-badge>
          <span class="text-muted small ml-50">
            инициатор: {{ detailInstance.initiator }} · старт: {{ detailInstance.started_at }}<span v-if="detailInstance.finished_at"> · финиш: {{ detailInstance.finished_at }}</span>
          </span>
        </div>

      <h6 class="mt-1">
        Этапы <small class="text-muted">— нажмите этап, чтобы увидеть данные</small>
      </h6>
      <div class="d-flex flex-wrap align-items-center mb-1">
        <template v-for="(n, idx) in detail.nodes">
          <div
            :key="`n${n.stage_def_id}`"
            class="bpm-node text-center px-1 py-50 mr-50 mb-50 rounded cursor-pointer"
            :class="[nodeClass(n), selectedNode && selectedNode.stage_def_id === n.stage_def_id ? 'bpm-node-sel' : '']"
            @click="selectNode(n)"
          >
            <div class="font-weight-bold">
              {{ n.label }}
            </div>
            <small>{{ n.status_code ? cubus.bpmStatusLabel(n.status_code) : 'не достигнут' }}</small>
          </div>
          <feather-icon
            v-if="idx < detail.nodes.length - 1"
            :key="`a${n.stage_def_id}`"
            icon="ArrowRightIcon"
            class="mr-50 mb-50 text-muted"
          />
        </template>
      </div>

      <!-- Детали выбранного этапа: что вошло / что вышло -->
      <div
        v-if="selectedNode"
        class="border rounded p-1 mb-1 bg-light-secondary"
      >
        <div class="d-flex justify-content-between align-items-center mb-50">
          <h6 class="mb-0">
            {{ selectedNode.label }}
            <b-badge
              v-if="selectedNode.status_code"
              :variant="cubus.bpmStatusVariant(selectedNode.status_code)"
              class="ml-50"
            >
              {{ cubus.bpmStatusLabel(selectedNode.status_code) }}
            </b-badge>
            <b-badge
              v-else
              variant="light-secondary"
              class="ml-50"
            >
              не достигнут
            </b-badge>
          </h6>
          <feather-icon
            icon="XIcon"
            class="cursor-pointer text-muted"
            @click="selectedNode = null"
          />
        </div>
        <div
          v-if="selectedNode.descr"
          class="small text-muted mb-50"
        >
          {{ selectedNode.descr }}
        </div>
        <div class="small mb-50">
          <span v-if="parseSids(selectedNode.assignee_sids).length">
            Исполнители: {{ parseSids(selectedNode.assignee_sids).join(', ') }}
          </span>
          <span v-if="selectedNode.claimed_by"> · в работе: {{ selectedNode.claimed_by }}</span>
          <span v-if="selectedNode.deadline"> · срок: {{ selectedNode.deadline }}</span>
          <span v-if="!parseSids(selectedNode.assignee_sids).length && !selectedNode.status_code" class="text-muted">этап ещё не запускался</span>
        </div>

        <b-row>
          <b-col md="6">
            <div class="font-weight-bold small mb-25">
              <feather-icon icon="LogInIcon" size="14" /> Вход (получено)
            </div>
            <b-table
              v-if="(selectedNode.inputs || []).length"
              small
              borderless
              :fields="slotFields"
              :items="selectedNode.inputs"
              class="mb-0"
            />
            <div
              v-else
              class="text-muted small"
            >
              нет входных данных
            </div>
          </b-col>
          <b-col md="6">
            <div class="font-weight-bold small mb-25">
              <feather-icon icon="LogOutIcon" size="14" /> Выход (заполнено)
            </div>
            <b-table
              v-if="(selectedNode.outputs || []).length"
              small
              borderless
              :fields="slotFields"
              :items="selectedNode.outputs"
              class="mb-0"
            />
            <div
              v-else
              class="text-muted small"
            >
              нет выходных данных
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Хронология — свёрнута по умолчанию -->
      <app-collapse type="margin">
        <app-collapse-item :title="`Хронология (${(detail.audit || []).length})`">
          <b-table
            small
            :fields="auditFields"
            :items="detail.audit"
            empty-text="Нет событий"
            show-empty
          >
            <template #cell(event_code)="data">
              <b-badge variant="light-secondary">
                {{ data.value }}
              </b-badge>
            </template>
          </b-table>
        </app-collapse-item>
      </app-collapse>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BTable, BButton, BBadge, BFormSelect, BSpinner, BRow, BCol, BModal, BPagination,
} from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'

export default {
  name: 'CbsBpmMonitor',
  components: {
    BCard, BTable, BButton, BBadge, BFormSelect, BSpinner, BRow, BCol, BModal, BPagination,
    AppCollapse, AppCollapseItem,
  },
  data() {
    return {
      cubus: useCubus,
      loading: false,
      currentPage: 1,
      perPage: 10,
      instances: [],
      detail: null,
      detailShow: false,
      selectedId: null,
      selectedNode: null,
      slotFields: [{ key: 'param_name', label: 'Поле' }, { key: 'value_json', label: 'Значение' }],
      statusFilter: '',
      statusOptions: [
        { value: '', text: 'Все статусы' },
        { value: 'ACTIVE', text: 'Активен' },
        { value: 'COMPLETED', text: 'Завершён' },
        { value: 'SUSPENDED', text: 'Приостановлен' },
        { value: 'ABORTED', text: 'Прерван' },
      ],
      fields: [
        { key: 'id', label: '#' },
        { key: 'proc_name', label: 'Процесс' },
        { key: 'status_code', label: 'Статус' },
        { key: 'initiator', label: 'Инициатор' },
        { key: 'started_at', label: 'Старт' },
        { key: 'active_stages', label: 'Активн. этапов' },
        { key: 'actions', label: '' },
      ],
      auditFields: [
        { key: 'event_at', label: 'Время' },
        { key: 'event_code', label: 'Событие' },
        { key: 'stage_label', label: 'Этап' },
        { key: 'usersid', label: 'Пользователь' },
      ],
    }
  },
  computed: {
    detailInstance() {
      return (this.detail && this.detail.instance) || {}
    },
  },
  created() {
    useCubus.setPageTitle('Мониторинг процессов')
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      const param = {}
      if (this.statusFilter) param.status = this.statusFilter
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method: 'bpm_instances', param, mode: 'sync' },
      })
        .then(response => {
          this.loading = false
          if (response.data.error) useCubus.toastError(this, response.data.error)
          else this.instances = (response.data.result && response.data.result.instances) || []
        })
        .catch(error => { this.loading = false; useCubus.toastError(this, error) })
    },
    openInstance(pi) {
      this.selectedId = pi
      this.selectedNode = null
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method: 'bpm_monitor', param: { process_instance_id: pi }, mode: 'sync' },
      })
        .then(response => {
          if (response.data.error) { useCubus.toastError(this, response.data.error); return }
          this.detail = response.data.result || null
          // авто-выбор текущего/последнего достигнутого этапа
          const reached = (this.detail && this.detail.nodes || []).filter(n => n.status_code)
          this.selectedNode = reached.length ? reached[reached.length - 1] : null
          this.detailShow = true
        })
        .catch(error => { useCubus.toastError(this, error) })
    },
    selectNode(n) {
      this.selectedNode = (this.selectedNode && this.selectedNode.stage_def_id === n.stage_def_id) ? null : n
    },
    parseSids(json) {
      if (!json) return []
      try { const a = JSON.parse(json); return Array.isArray(a) ? a : [] } catch (e) { return [] }
    },
    nodeClass(n) {
      const s = n.status_code
      if (s === 'ACTIVE' || s === 'ASSIGNED' || s === 'IN_PROGRESS') return 'bg-light-primary border-primary'
      if (s === 'COMPLETED') return 'bg-light-success border-success'
      if (s === 'ABORTED' || s === 'SUSPENDED') return 'bg-light-danger border-danger'
      return 'bg-light-secondary text-muted'
    },
    rowClass(item) {
      return item && item.id === this.selectedId ? 'table-active' : ''
    },
  },
}
</script>

<style scoped>
.bpm-node {
  min-width: 96px;
  border: 1px solid transparent;
  transition: box-shadow .15s;
}
.bpm-node:hover {
  box-shadow: 0 0 0 2px rgba(115, 103, 240, .25);
}
.bpm-node-sel {
  box-shadow: 0 0 0 2px #7367f0;
}
</style>
